import { FormEvent, ReactNode, useEffect, useMemo, useState } from "react";
import { api } from "../api/client";
import { useAuth } from "../auth/AuthContext";
import { useTheme } from "../theme/ThemeContext";
import RouteTrackMap, { type TrackSegment, type TrackStop } from "../components/RouteTrackMap";
import type {
  ListResponse,
  Route,
  RouteShape,
  RouteStopTemplate,
  RouteStopTemplateStop,
  Stop,
  TripInstance,
} from "../api/types";

type Direction = "upward" | "downward";
type Tab = "overview" | "routes" | "stops" | "templates";

const ROUTE_TYPE_LABELS: Record<number, string> = {
  0: "Tram/Light Rail", 1: "Subway/Metro", 2: "Rail", 3: "Bus",
  4: "Ferry", 5: "Cable car", 6: "Aerial lift", 7: "Funicular",
  11: "Trolleybus", 12: "Monorail",
};

function unwrap<T>(value: ListResponse<T> | T[] | undefined | null): T[] {
  if (!value) return [];
  if (Array.isArray(value)) return value;
  if (Array.isArray((value as ListResponse<T>).data)) return value.data;
  return [];
}

function downloadBlob(blob: Blob, filename: string) {
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  URL.revokeObjectURL(url);
}

// Great-circle distance in km between two lat/lon points.
function haversineKm(a: { lat: number; lon: number }, b: { lat: number; lon: number }): number {
  const R = 6371;
  const dLat = ((b.lat - a.lat) * Math.PI) / 180;
  const dLon = ((b.lon - a.lon) * Math.PI) / 180;
  const la1 = (a.lat * Math.PI) / 180;
  const la2 = (b.lat * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2 + Math.cos(la1) * Math.cos(la2) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.asin(Math.sqrt(h));
}

function lineLabel(r: Route): string {
  return r.route_short_name || r.route_id;
}

function lineAbbr(label: string): string {
  const parts = label.split(/\s+/).filter(Boolean);
  const abbr = parts.length >= 2 ? parts[0][0] + parts[1][0] : label.slice(0, 2);
  return abbr.toUpperCase();
}

function routeLabel(r: Route): string {
  return `${r.route_short_name ?? r.route_id} — ${r.route_long_name ?? ""}`.trim();
}

// ── Small shared building blocks ─────────────────────────────────────────────

function LineBadge({ label, color }: { label: string; color: string }) {
  return (
    <span className="line-badge" style={{ "--line-color": `#${color}` } as React.CSSProperties}>
      <span className="line-badge-avatar">{lineAbbr(label)}</span>
      {label}
    </span>
  );
}

function PillTrack<T extends string>({
  options, value, onChange, size = "md",
}: {
  options: { value: T; label: ReactNode; disabled?: boolean; count?: number }[];
  value: T;
  onChange: (v: T) => void;
  size?: "md" | "sm";
}) {
  return (
    <div className={"pill-track" + (size === "sm" ? " sm" : "")}>
      {options.map((o) => (
        <button
          key={o.value}
          type="button"
          className={"pill-track-btn" + (value === o.value ? " active" : "")}
          disabled={o.disabled}
          onClick={() => onChange(o.value)}
        >
          {o.label}
          {o.count != null && <span className="count-badge">{o.count}</span>}
        </button>
      ))}
    </div>
  );
}

function SectionCard({
  title, subtitle, right, children,
}: { title: string; subtitle?: string; right?: ReactNode; children: ReactNode }) {
  return (
    <section className="network-card">
      <div className="network-card-head">
        <div className="network-card-title-wrap">
          <span className="network-card-bar" />
          <h3>{title}</h3>
          {subtitle && <span className="network-card-sub">{subtitle}</span>}
        </div>
        {right && <div className="network-card-right">{right}</div>}
      </div>
      {children}
    </section>
  );
}

export default function StopTemplatesPage() {
  const { session } = useAuth();
  const role = session?.role?.toLowerCase() ?? "";
  const canManage = role === "admin" || role === "operations_supervisor";
  const agencyId = session?.agencyId ?? "";

  const [tab, setTab] = useState<Tab>("overview");
  const [templates, setTemplates] = useState<RouteStopTemplate[]>([]);
  const [routes, setRoutes] = useState<Route[]>([]);
  const [stops, setStops] = useState<Stop[]>([]);
  const [activeTrips, setActiveTrips] = useState<TripInstance[]>([]);
  const [routeShapes, setRouteShapes] = useState<RouteShape[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [openCreateRouteSignal, setOpenCreateRouteSignal] = useState(0);

  const [routeId, setRouteId] = useState("");
  const [direction, setDirection] = useState<Direction>("upward");
  const [headsign, setHeadsign] = useState("");
  const [notes, setNotes] = useState("");
  const [draft, setDraft] = useState<RouteStopTemplateStop[]>([]);

  async function loadAll() {
    setLoading(true);
    try {
      const [tpls, rt, st, trips, shapes] = await Promise.all([
        api.listStopTemplates(),
        api.listRoutes(),
        api.listStops(),
        api.listTripInstances({ status: "in_progress" }).catch(() => []),
        api.listRouteShapes().catch(() => []),
      ]);
      setTemplates(tpls ?? []);
      setRoutes(unwrap(rt));
      setStops(unwrap(st));
      setActiveTrips(Array.isArray(trips) ? trips : []);
      setRouteShapes(Array.isArray(shapes) ? shapes : []);
      setError(null);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Load failed");
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    loadAll();
  }, []);

  const stopById = useMemo(() => {
    const m = new Map<string, Stop>();
    stops.forEach((s) => m.set(s.id, s));
    return m;
  }, [stops]);

  const routeById = useMemo(() => {
    const m = new Map<string, Route>();
    routes.forEach((r) => m.set(r.id, r));
    return m;
  }, [routes]);

  // Which line (route_short_name) each stop belongs to, via whichever
  // template references it first — used for the colored dot in Stops and
  // the schematic map. A stop with no template yet has no line color.
  const lineColorByStopId = useMemo(() => {
    const m = new Map<string, string>();
    for (const t of templates) {
      const rt = routeById.get(t.route_id);
      if (!rt) continue;
      const color = rt.route_color || "888888";
      for (const s of t.stops ?? []) if (!m.has(s.stop_id)) m.set(s.stop_id, color);
    }
    return m;
  }, [templates, routeById]);

  const lines = useMemo(() => {
    const m = new Map<string, { label: string; color: string; routes: Route[] }>();
    for (const r of routes) {
      const key = lineLabel(r);
      const entry = m.get(key) ?? { label: key, color: r.route_color || "888888", routes: [] };
      entry.routes.push(r);
      m.set(key, entry);
    }
    return [...m.values()];
  }, [routes]);

  function loadIntoEditor(t: RouteStopTemplate) {
    setRouteId(t.route_id);
    setDirection(t.direction.toLowerCase() as Direction);
    setHeadsign(t.headsign ?? "");
    setNotes(t.notes ?? "");
    setDraft(
      (t.stops ?? []).map((s) => ({
        stop_sequence: s.stop_sequence,
        stop_id: s.stop_id,
        offset_seconds: s.offset_seconds,
        dwell_seconds: s.dwell_seconds,
      }))
    );
    setTab("templates");
    setError(null);
    setSuccess(null);
  }

  function addRow() {
    const nextSeq = draft.length === 0 ? 1 : Math.max(...draft.map((d) => d.stop_sequence)) + 1;
    setDraft([
      ...draft,
      { stop_sequence: nextSeq, stop_id: stops[0]?.id ?? "", offset_seconds: 0, dwell_seconds: 0 },
    ]);
  }

  function updateRow(idx: number, patch: Partial<RouteStopTemplateStop>) {
    setDraft(draft.map((r, i) => (i === idx ? { ...r, ...patch } : r)));
  }

  function removeRow(idx: number) {
    setDraft(draft.filter((_, i) => i !== idx).map((r, i) => ({ ...r, stop_sequence: i + 1 })));
  }

  function moveRow(idx: number, dir: -1 | 1) {
    const next = [...draft];
    const j = idx + dir;
    if (j < 0 || j >= next.length) return;
    [next[idx], next[j]] = [next[j], next[idx]];
    setDraft(next.map((r, i) => ({ ...r, stop_sequence: i + 1 })));
  }

  function newTemplate() {
    setRouteId("");
    setDirection("upward");
    setHeadsign("");
    setNotes("");
    setDraft([]);
    setError(null);
    setSuccess(null);
  }

  async function onSave(e: FormEvent) {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!routeId) {
      setError("Pick a route");
      return;
    }
    if (draft.length === 0) {
      setError("Add at least one stop");
      return;
    }
    try {
      await api.upsertStopTemplate({
        route_id: routeId,
        direction,
        headsign: headsign || undefined,
        notes: notes || undefined,
        stops: draft.map((r, i) => ({ ...r, stop_sequence: i + 1 })),
      });
      setSuccess("Template saved");
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Save failed");
    }
  }

  async function onDeleteTemplate(id: string) {
    if (!confirm("Delete this template?")) return;
    try {
      await api.deleteStopTemplate(id);
      setSuccess("Deleted");
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Delete failed");
    }
  }

  async function onDeriveTemplates() {
    if (!confirm("Derive templates from existing GTFS trips? This replaces any current templates for routes that have a direction-0 trip with stop_times.")) return;
    try {
      const r = await api.deriveStopTemplates();
      setSuccess(
        `Derived ${r.templates_created} template(s) across ${r.routes_processed} route(s)` +
        (r.routes_skipped ? `; ${r.routes_skipped} skipped (no trips)` : "")
      );
      await loadAll();
    } catch (err) {
      setError(err instanceof Error ? err.message : "Derive failed");
    }
  }

  async function onExportGtfs() {
    if (!agencyId) { setError("No agency in session"); return; }
    try {
      const blob = await api.exportGTFS(agencyId);
      downloadBlob(blob, `gtfs_${agencyId.slice(0, 8)}.zip`);
      setSuccess("GTFS feed exported");
    } catch (err) {
      setError(err instanceof Error ? err.message : "Export failed");
    }
  }

  // Every active route needs at least one stop template to be schedulable —
  // the same precondition the Schedule Generator enforces server-side.
  const routesMissingTemplate = useMemo(
    () => routes.filter((r) => r.is_active !== false && !templates.some((t) => t.route_id === r.id)),
    [routes, templates]
  );
  const gtfsInSync = routes.length > 0 && routesMissingTemplate.length === 0;

  const TAB_OPTIONS: { value: Tab; label: string; count: number }[] = [
    { value: "overview", label: "Overview", count: lines.length },
    { value: "routes", label: "Routes", count: routes.length },
    { value: "stops", label: "Stops", count: stops.length },
    { value: "templates", label: "Templates", count: templates.length },
  ];

  return (
    <div className="page network-page">
      <div className="network-head">
        <div>
          <h2>Network</h2>
          <p className="secondary-text">
            Routes, stops and per-direction stop templates. These power the Schedule Generator.
          </p>
        </div>
        {canManage && (
          <button type="button" className="btn-pill primary" onClick={() => { setTab("routes"); setOpenCreateRouteSignal((n) => n + 1); }}>
            + New route
          </button>
        )}
      </div>

      {!canManage && (
        <div className="banner banner-error">
          Only <code>admin</code> / <code>operations_supervisor</code> can edit network data.
        </div>
      )}
      {error && <div className="banner banner-error">{error}</div>}
      {success && <div className="banner banner-success">{success}</div>}

      <div className="network-tabs-row">
        <PillTrack
          options={TAB_OPTIONS.map((t) => ({ value: t.value, label: t.label, count: t.count }))}
          value={tab}
          onChange={(v) => { setTab(v); setError(null); setSuccess(null); }}
        />
        <div className="network-tabs-actions">
          <span className={"gtfs-sync" + (gtfsInSync ? "" : " warn")}>
            {gtfsInSync ? "✓ GTFS in sync" : `⚠ ${routesMissingTemplate.length} route(s) need templates`}
          </span>
          <button type="button" className="btn-pill" onClick={onExportGtfs} disabled={!agencyId}>
            ⬇ Export GTFS
          </button>
        </div>
      </div>

      {tab === "overview" && (
        <OverviewTab
          lines={lines}
          templates={templates}
          stopById={stopById}
          activeTrips={activeTrips}
          routeShapes={routeShapes}
          loading={loading}
        />
      )}

      {tab === "routes" && (
        <RoutesManager
          routes={routes}
          canManage={canManage}
          onReload={loadAll}
          onError={setError}
          onSuccess={setSuccess}
          openCreateSignal={openCreateRouteSignal}
        />
      )}

      {tab === "stops" && (
        <StopsManager
          stops={stops}
          lineColorByStopId={lineColorByStopId}
          canManage={canManage}
          onReload={loadAll}
          onError={setError}
          onSuccess={setSuccess}
        />
      )}

      {tab === "templates" && (
        <>
          <SectionCard
            title="Stop templates"
            subtitle="Per-direction stop sequences used by the Schedule Generator"
            right={canManage && (
              <button type="button" className="btn-pill" onClick={onDeriveTemplates}>
                ↻ Derive from existing trips
              </button>
            )}
          >
            {loading && <div className="secondary-text">Loading…</div>}
            <table className="data-table compact network-table">
              <thead>
                <tr>
                  <th>Route</th>
                  <th>Direction</th>
                  <th>Stops</th>
                  <th>Headsign</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {templates.length === 0 && (
                  <tr>
                    <td colSpan={5} className="empty">— none —</td>
                  </tr>
                )}
                {templates.map((t) => {
                  const rt = routeById.get(t.route_id);
                  const dir = t.direction.toLowerCase();
                  return (
                    <tr key={t.id}>
                      <td>
                        {rt ? <LineBadge label={lineLabel(rt)} color={rt.route_color || "888888"} /> : t.route_id}
                        {rt?.route_long_name && <span className="network-table-sub"> — {rt.route_long_name}</span>}
                      </td>
                      <td>
                        <span className={"dir-badge " + dir}>{dir === "upward" ? "↑" : "↓"} {dir}</span>
                      </td>
                      <td>{t.stops?.length ?? 0}</td>
                      <td>{t.headsign || "—"}</td>
                      <td>
                        <div className="row-actions">
                          <button className="btn-pill sm" onClick={() => loadIntoEditor(t)}>Edit</button>
                          {canManage && (
                            <button className="btn-pill sm danger" onClick={() => onDeleteTemplate(t.id)}>Delete</button>
                          )}
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </SectionCard>

          {canManage && (
            <SectionCard
              title="Edit / create template"
              subtitle="Sequence stops with offset and dwell time"
              right={draft.length > 0 && (
                <button type="button" className="btn-pill sm" onClick={newTemplate}>Clear</button>
              )}
            >
              <form className="form-grid" onSubmit={onSave}>
                <div className="form-row">
                  <label>
                    Route
                    <select value={routeId} onChange={(e) => setRouteId(e.target.value)}>
                      <option value="">— pick a route —</option>
                      {routes.map((r) => (
                        <option key={r.id} value={r.id}>{routeLabel(r)}</option>
                      ))}
                    </select>
                  </label>
                  <label>
                    Direction
                    <PillTrack
                      options={[
                        { value: "upward" as Direction, label: "↑ Upward" },
                        { value: "downward" as Direction, label: "↓ Downward" },
                      ]}
                      value={direction}
                      onChange={setDirection}
                    />
                  </label>
                  <label>
                    Headsign
                    <input value={headsign} onChange={(e) => setHeadsign(e.target.value)} placeholder="e.g. Faiz Ahmed Faiz to N-5" />
                  </label>
                </div>
                <div className="form-row">
                  <label style={{ flex: 1 }}>
                    Notes
                    <input value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Optional operating notes for this template" />
                  </label>
                </div>

                <h4 className="network-subhead">Stop sequence <span className="count-badge">{draft.length} stops</span></h4>
                <table className="data-table compact network-table">
                  <thead>
                    <tr>
                      <th>#</th>
                      <th>Stop</th>
                      <th>Offset (s)</th>
                      <th>Dwell (s)</th>
                      <th></th>
                    </tr>
                  </thead>
                  <tbody>
                    {draft.length === 0 && (
                      <tr>
                        <td colSpan={5} className="empty">— empty —</td>
                      </tr>
                    )}
                    {draft.map((row, idx) => {
                      const stop = stopById.get(row.stop_id);
                      return (
                        <tr key={idx}>
                          <td>{idx + 1}</td>
                          <td>
                            <select
                              value={row.stop_id}
                              onChange={(e) => updateRow(idx, { stop_id: e.target.value })}
                            >
                              <option value="">— pick stop —</option>
                              {stops.map((s) => (
                                <option key={s.id} value={s.id}>{s.stop_id} · {s.stop_name}</option>
                              ))}
                            </select>
                            {!stop && row.stop_id && (
                              <span className="secondary-text"> (unknown)</span>
                            )}
                          </td>
                          <td>
                            <input
                              type="number"
                              min={0}
                              value={row.offset_seconds}
                              onChange={(e) => updateRow(idx, { offset_seconds: parseInt(e.target.value || "0", 10) })}
                              style={{ width: 100 }}
                            />
                          </td>
                          <td>
                            <input
                              type="number"
                              min={0}
                              value={row.dwell_seconds}
                              onChange={(e) => updateRow(idx, { dwell_seconds: parseInt(e.target.value || "0", 10) })}
                              style={{ width: 100 }}
                            />
                          </td>
                          <td>
                            <div style={{ display: "flex", gap: 4 }}>
                              <button type="button" className="btn ghost" onClick={() => moveRow(idx, -1)}>↑</button>
                              <button type="button" className="btn ghost" onClick={() => moveRow(idx, 1)}>↓</button>
                              <button type="button" className="btn ghost" onClick={() => removeRow(idx)}>✕</button>
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
                <div className="form-row">
                  <button type="button" className="btn-pill" onClick={addRow}>+ Add stop</button>
                  <button type="submit" className="btn-pill primary">Save template</button>
                </div>
              </form>
            </SectionCard>
          )}
        </>
      )}
    </div>
  );
}

// ── OverviewTab ──────────────────────────────────────────────────────────────

function OverviewTab({
  lines, templates, stopById, activeTrips, routeShapes, loading,
}: {
  lines: { label: string; color: string; routes: Route[] }[];
  templates: RouteStopTemplate[];
  stopById: Map<string, Stop>;
  activeTrips: TripInstance[];
  routeShapes: RouteShape[];
  loading: boolean;
}) {
  const { theme } = useTheme();
  const [mapFilter, setMapFilter] = useState<string>("all");
  const [focusedLine, setFocusedLine] = useState<string | null>(null);
  const [selectedDir, setSelectedDir] = useState<Direction>("upward");

  useEffect(() => {
    if (!focusedLine && lines.length > 0) setFocusedLine(lines[0].label);
  }, [lines, focusedLine]);

  const active = lines.find((l) => l.label === focusedLine) ?? lines[0];

  // Every stop, in per-direction sequence, that belongs to the given line's
  // route segments — concatenated in route order so a two-segment line (e.g.
  // Orange Line's FAF↔N-5 and N-5↔Airport records) draws as one continuous
  // path on the map.
  function stopsForLine(line: { routes: Route[] }, dir: Direction): Stop[] {
    const out: Stop[] = [];
    // Route segments are stored outbound-first (e.g. Orange Line's FAF↔N-5
    // then N-5↔Airport). Their own downward templates already run backwards
    // within each segment, so a coherent Return path needs the SEGMENTS
    // reversed too — otherwise it jumps from one segment's tail straight to
    // the next segment's head instead of retracing the outbound order.
    const orderedRoutes = dir === "downward" ? [...line.routes].reverse() : line.routes;
    for (const r of orderedRoutes) {
      const t = templates.find((tt) => tt.route_id === r.id && tt.direction.toLowerCase() === dir);
      if (!t) continue;
      for (const s of [...(t.stops ?? [])].sort((a, b) => a.stop_sequence - b.stop_sequence)) {
        const stop = stopById.get(s.stop_id);
        // Skip the duplicate junction stop where two route segments of the
        // same line meet (e.g. Orange Line's FAF↔N-5 and N-5↔Airport each
        // have their own "N-5" stop record for the same physical junction)
        // so it doesn't render twice back-to-back in this view.
        if (stop && stop.stop_name !== out[out.length - 1]?.stop_name) out.push(stop);
      }
    }
    return out;
  }

  const drawnLines = mapFilter === "all" ? lines : lines.filter((l) => l.label === mapFilter);

  // Real GTFS shape (actual road-following polyline) per drawn line, when one
  // exists; otherwise fall back to a straight path through its real stops —
  // still 100% real coordinates, just without the road-following detail.
  const trackSegments = useMemo<TrackSegment[]>(() => {
    const out: TrackSegment[] = [];
    for (const l of drawnLines) {
      const routeIds = new Set(l.routes.map((r) => r.id));
      const shapes = routeShapes.filter((s) => routeIds.has(s.route_id));
      if (shapes.length > 0) {
        for (const s of shapes) out.push({ id: s.shape_id, coordinates: s.coordinates, color: l.color });
      } else {
        const pts = stopsForLine(l, "upward");
        if (pts.length >= 2) {
          out.push({
            id: `${l.label}-fallback`,
            coordinates: pts.map((s) => [s.location.latitude, s.location.longitude]),
            color: l.color,
          });
        }
      }
    }
    return out;
  }, [drawnLines, routeShapes, templates, stopById]);

  const mapStops = useMemo<TrackStop[]>(() => {
    const out: TrackStop[] = [];
    for (const l of drawnLines) {
      const pts = stopsForLine(l, "upward");
      pts.forEach((s, i) => {
        out.push({
          id: s.id, name: s.stop_name, lat: s.location.latitude, lon: s.location.longitude,
          color: l.color, isEndpoint: i === 0 || i === pts.length - 1,
        });
      });
    }
    return out;
  }, [drawnLines, templates, stopById]);

  const activeTemplates = useMemo(
    () => templates.filter((t) => active?.routes.some((r) => r.id === t.route_id)), [templates, active]
  );
  const hasUp = activeTemplates.some((t) => t.direction.toLowerCase() === "upward");
  const hasDown = activeTemplates.some((t) => t.direction.toLowerCase() === "downward");
  const sidebarStops = useMemo(
    () => (active ? stopsForLine(active, selectedDir) : []), [active, selectedDir, templates, stopById]
  );
  const cumulativeKm = useMemo(() => {
    const out: number[] = [];
    let total = 0;
    for (let i = 0; i < sidebarStops.length; i++) {
      if (i > 0) {
        const a = sidebarStops[i - 1].location;
        const b = sidebarStops[i].location;
        total += haversineKm({ lat: a.latitude, lon: a.longitude }, { lat: b.latitude, lon: b.longitude });
      }
      out.push(total);
    }
    return out;
  }, [sidebarStops]);

  const lineStationCount = useMemo(() => {
    if (!active) return 0;
    const ids = new Set<string>();
    for (const r of active.routes) {
      for (const t of templates.filter((tt) => tt.route_id === r.id)) {
        for (const s of t.stops ?? []) ids.add(s.stop_id);
      }
    }
    return ids.size;
  }, [active, templates]);

  const busesRunning = useMemo(() => {
    if (!active) return 0;
    return activeTrips.filter((t) => t.route_short_name === active.label).length;
  }, [activeTrips, active]);

  if (loading && lines.length === 0) {
    return <div className="secondary-text" style={{ marginTop: 16 }}>Loading…</div>;
  }
  if (lines.length === 0) {
    return (
      <div className="network-card" style={{ marginTop: 12 }}>
        <p className="secondary-text">
          No routes defined yet. Switch to the Routes tab to add the agency's first line.
        </p>
      </div>
    );
  }

  return (
    <div className="network-overview">
      <div className="network-filter-bar">
        <div className="network-filter-group">
          <span className="network-filter-label">Route</span>
          <div className="route-chip-row">
            {lines.length > 1 && (
              <button
                type="button"
                className={"route-chip" + (mapFilter === "all" ? " active" : "")}
                onClick={() => setMapFilter("all")}
              >
                <span className="route-chip-dot all" /> All lines
              </button>
            )}
            {lines.map((l) => (
              <button
                key={l.label}
                type="button"
                className={"route-chip" + (mapFilter === l.label ? " active" : "")}
                onClick={() => { setMapFilter(l.label); setFocusedLine(l.label); setSelectedDir("upward"); }}
              >
                <span className="route-chip-dot" style={{ background: `#${l.color}` }} /> {l.label}
              </button>
            ))}
          </div>
        </div>
        <div className="network-filter-group">
          <span className="network-filter-label">Direction</span>
          <PillTrack
            size="sm"
            options={[
              { value: "upward" as Direction, label: "Outbound", disabled: !hasUp },
              { value: "downward" as Direction, label: "Return", disabled: !hasDown },
            ]}
            value={selectedDir}
            onChange={setSelectedDir}
          />
        </div>
        <div className="network-filter-spacer" />
        {active && (
          <div className="network-stat-pills">
            <span className="stat-pill line" style={{ "--line-color": `#${active.color}` } as React.CSSProperties}>
              {active.label.toUpperCase()}
            </span>
            <span className="stat-pill info">{lineStationCount} STATIONS</span>
            <span className="stat-pill ok">{busesRunning} BUSES RUNNING</span>
          </div>
        )}
      </div>

      <div className="network-overview-grid">
        <div className="network-map-card">
          {mapStops.length === 0 ? (
            <p className="secondary-text" style={{ padding: 16 }}>
              No stop templates yet for the selected line(s) — add one from the Templates tab.
            </p>
          ) : (
            <RouteTrackMap segments={trackSegments} stops={mapStops} theme={theme} />
          )}
        </div>

        {active && (
          <aside className="network-side-card">
            <div className="network-side-head">
              <div className="network-side-head-title">
                <span className="network-side-dot" style={{ background: `#${active.color}` }} />
                <h3>{active.label}</h3>
              </div>
              <span className={"dir-badge " + selectedDir}>{selectedDir === "upward" ? "Outbound" : "Return"}</span>
            </div>
            {sidebarStops.length === 0 ? (
              <p className="secondary-text">No stop template for this direction yet.</p>
            ) : (
              <div className="network-side-list">
                <div className="network-side-line" style={{ background: `#${active.color}` }} />
                {sidebarStops.map((s, i) => {
                  const isFirst = i === 0;
                  const isLast = i === sidebarStops.length - 1;
                  return (
                    <div className={"network-side-stop" + (isFirst || isLast ? " endpoint" : "")} key={s.id}>
                      <span
                        className="network-side-index"
                        style={
                          isFirst || isLast
                            ? { background: `#${active.color}`, borderColor: `#${active.color}`, color: "#fff" }
                            : { borderColor: `#${active.color}` }
                        }
                      >
                        {i + 1}
                      </span>
                      <div className="network-side-info">
                        <span className="network-side-name">{s.stop_name}</span>
                        {isFirst && <span className="network-side-tag">Start</span>}
                        {isLast && <span className="network-side-tag">End</span>}
                      </div>
                      <span className="network-side-km">{cumulativeKm[i].toFixed(1)} km</span>
                    </div>
                  );
                })}
              </div>
            )}
          </aside>
        )}
      </div>
    </div>
  );
}

// ── RoutesManager ────────────────────────────────────────────────────────────

const emptyRouteDraft = { routeId: "", shortName: "", longName: "", routeType: 3, color: "3F51B5", textColor: "FFFFFF" };

function RoutesManager({
  routes, canManage, onReload, onError, onSuccess, openCreateSignal,
}: {
  routes: Route[];
  canManage: boolean;
  onReload: () => Promise<void> | void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
  openCreateSignal: number;
}) {
  const [showCreate, setShowCreate] = useState(routes.length === 0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyRouteDraft);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (routes.length === 0) setShowCreate(true);
  }, [routes.length]);

  useEffect(() => {
    if (openCreateSignal > 0) { setEditingId(null); setForm(emptyRouteDraft); setShowCreate(true); }
  }, [openCreateSignal]);

  function startEdit(r: Route) {
    setEditingId(r.id);
    setForm({
      routeId: r.route_id,
      shortName: r.route_short_name ?? "",
      longName: r.route_long_name ?? "",
      routeType: r.route_type ?? 3,
      color: r.route_color ?? "3F51B5",
      textColor: r.route_text_color ?? "FFFFFF",
    });
    setShowCreate(true);
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyRouteDraft);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.routeId.trim()) { onError("route_id required"); return; }
    if (!form.shortName.trim() && !form.longName.trim()) {
      onError("Enter route_short_name or route_long_name"); return;
    }
    setBusy(true);
    try {
      const body: Partial<Route> = {
        route_id: form.routeId.trim(),
        route_short_name: form.shortName.trim() || undefined,
        route_long_name: form.longName.trim() || undefined,
        route_type: form.routeType,
        route_color: form.color.replace(/^#/, ""),
        route_text_color: form.textColor.replace(/^#/, ""),
      };
      if (editingId) {
        await api.updateRoute(editingId, body);
        onSuccess(`Route ${form.routeId} updated`);
      } else {
        await api.createRoute(body);
        onSuccess(`Route ${form.routeId} created`);
      }
      resetForm();
      await onReload();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Save route failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(r: Route) {
    if (!confirm(`Delete route ${r.route_short_name || r.route_id}? Trips referencing it will lose the link.`)) return;
    try {
      await api.deleteRoute(r.id);
      onSuccess(`Route ${r.route_id} deleted`);
      await onReload();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Delete route failed");
    }
  }

  return (
    <SectionCard
      title="Routes"
      subtitle={`${routes.length} defined · GTFS route entities`}
      right={canManage && !showCreate && (
        <button type="button" className="btn-pill sm" onClick={() => setShowCreate(true)}>+ Add route</button>
      )}
    >
      {routes.length === 0 && (
        <p className="secondary-text" style={{ marginTop: 8 }}>
          No routes defined yet. Add at least one route below, then create stops and a stop template per direction.
        </p>
      )}

      {routes.length > 0 && (
        <table className="data-table compact network-table">
          <thead>
            <tr>
              <th>route_id</th><th>Short</th><th>Long name</th><th>Type</th><th>Color</th><th>Active</th><th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {routes.map((r) => (
              <tr key={r.id}>
                <td><code>{r.route_id}</code></td>
                <td>{r.route_short_name ? <LineBadge label={r.route_short_name} color={r.route_color || "888888"} /> : "—"}</td>
                <td>{r.route_long_name || "—"}</td>
                <td>{(r.route_type ?? 3)} — {ROUTE_TYPE_LABELS[r.route_type ?? 3] ?? "Other"}</td>
                <td>
                  <span className="color-swatch" style={{ background: `#${r.route_color || "888"}` }} />
                  <code style={{ fontSize: "0.8em" }}>#{r.route_color || "—"}</code>
                </td>
                <td>
                  <span className={"status-dot" + (r.is_active === false ? " off" : "")} /> {r.is_active === false ? "Inactive" : "Active"}
                </td>
                <td>
                  <div className="row-actions">
                    {canManage && <button className="btn-pill sm" onClick={() => startEdit(r)}>Edit</button>}
                    {canManage && <button className="btn-pill sm danger" onClick={() => onDelete(r)}>Delete</button>}
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {canManage && showCreate && (
        <form className="form-grid network-form" onSubmit={onSubmit}>
          <div className="network-form-label-row">
            <span className="network-form-label">{editingId ? "Edit route" : "Add route"}</span>
            <button type="button" className="btn-pill sm" onClick={() => { resetForm(); setShowCreate(false); }}>
              {editingId ? "Cancel" : "Close"}
            </button>
          </div>
          <div className="form-row">
            <label>
              route_id (GTFS)
              <input value={form.routeId} onChange={(e) => setForm({ ...form, routeId: e.target.value })} placeholder="N5" required />
            </label>
            <label>
              Short name
              <input value={form.shortName} onChange={(e) => setForm({ ...form, shortName: e.target.value })} placeholder="N5" />
            </label>
            <label style={{ flex: 2 }}>
              Long name
              <input value={form.longName} onChange={(e) => setForm({ ...form, longName: e.target.value })} placeholder="Faisal Avenue Express" />
            </label>
          </div>
          <div className="form-row">
            <label>
              Type (GTFS)
              <select value={form.routeType} onChange={(e) => setForm({ ...form, routeType: Number(e.target.value) })}>
                {Object.entries(ROUTE_TYPE_LABELS).map(([v, l]) => (
                  <option key={v} value={v}>{v} — {l}</option>
                ))}
              </select>
            </label>
            <label>
              Route color
              <span className="color-field">
                <span className="color-swatch" style={{ background: `#${form.color}` }} />
                <input value={form.color} onChange={(e) => setForm({ ...form, color: e.target.value })} placeholder="3F51B5" />
              </span>
            </label>
            <label>
              Text color
              <span className="color-field">
                <span className="color-swatch" style={{ background: `#${form.textColor}` }} />
                <input value={form.textColor} onChange={(e) => setForm({ ...form, textColor: e.target.value })} placeholder="FFFFFF" />
              </span>
            </label>
            <button type="submit" className="btn-pill primary" disabled={busy} style={{ alignSelf: "end" }}>
              {busy ? "Saving…" : editingId ? "Save changes" : "+ Add route"}
            </button>
          </div>
        </form>
      )}
    </SectionCard>
  );
}

// ── StopsManager ─────────────────────────────────────────────────────────────

const emptyStopDraft = { stopId: "", stopCode: "", stopName: "", lat: "", lon: "" };

function StopsManager({
  stops, lineColorByStopId, canManage, onReload, onError, onSuccess,
}: {
  stops: Stop[];
  lineColorByStopId: Map<string, string>;
  canManage: boolean;
  onReload: () => Promise<void> | void;
  onError: (msg: string) => void;
  onSuccess: (msg: string) => void;
}) {
  const [showCreate, setShowCreate] = useState(stops.length === 0);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [form, setForm] = useState(emptyStopDraft);
  const [busy, setBusy] = useState(false);
  const [filter, setFilter] = useState("");

  useEffect(() => {
    if (stops.length === 0) setShowCreate(true);
  }, [stops.length]);

  const lineCount = useMemo(() => new Set(lineColorByStopId.values()).size, [lineColorByStopId]);
  const filtered = useMemo(
    () => stops.filter((s) =>
      !filter || s.stop_id.toLowerCase().includes(filter.toLowerCase()) || s.stop_name.toLowerCase().includes(filter.toLowerCase())
    ),
    [stops, filter]
  );

  function startEdit(s: Stop) {
    setEditingId(s.id);
    setForm({
      stopId: s.stop_id, stopCode: s.stop_code ?? "", stopName: s.stop_name,
      lat: String(s.location?.latitude ?? ""), lon: String(s.location?.longitude ?? ""),
    });
    setShowCreate(true);
  }

  function resetForm() {
    setEditingId(null);
    setForm(emptyStopDraft);
  }

  async function onSubmit(e: FormEvent) {
    e.preventDefault();
    if (!form.stopId.trim() || !form.stopName.trim()) { onError("stop_id and stop_name required"); return; }
    const latN = Number(form.lat);
    const lonN = Number(form.lon);
    if (!Number.isFinite(latN) || !Number.isFinite(lonN)) { onError("lat/lon must be numbers"); return; }
    if (latN < -90 || latN > 90 || lonN < -180 || lonN > 180) { onError("lat/lon out of range"); return; }
    setBusy(true);
    try {
      const body = {
        stop_id: form.stopId.trim(),
        stop_code: form.stopCode.trim() || undefined,
        stop_name: form.stopName.trim(),
        stop_lat: latN,
        stop_lon: lonN,
      };
      if (editingId) {
        await api.updateStop(editingId, body);
        onSuccess(`Stop ${form.stopId} updated`);
      } else {
        await api.createStop(body);
        onSuccess(`Stop ${form.stopId} created`);
      }
      resetForm();
      await onReload();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Save stop failed");
    } finally {
      setBusy(false);
    }
  }

  async function onDelete(s: Stop) {
    if (!confirm(`Delete stop ${s.stop_name}? Trips referencing it via stop_times will fail to materialize.`)) return;
    try {
      await api.deleteStop(s.id);
      onSuccess(`Stop ${s.stop_id} deleted`);
      await onReload();
    } catch (err) {
      onError(err instanceof Error ? err.message : "Delete stop failed");
    }
  }

  return (
    <SectionCard
      title="Stops"
      subtitle={`${stops.length} stops across ${lineCount || 0} line${lineCount === 1 ? "" : "s"}`}
      right={
        <div className="network-search">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2"><circle cx="11" cy="11" r="7" /><path d="M21 21l-4.3-4.3" /></svg>
          <input value={filter} onChange={(e) => setFilter(e.target.value)} placeholder="Filter by stop name or ID" />
        </div>
      }
    >
      {stops.length === 0 && (
        <p className="secondary-text" style={{ marginTop: 8 }}>
          No stops yet. Add the stops that comprise each direction of every route, then assemble them into a stop template below.
        </p>
      )}

      {stops.length > 0 && (
        <div className="network-scroll-table">
          <table className="data-table compact network-table">
            <thead>
              <tr>
                <th>stop_id</th><th>Code</th><th>Name</th><th>Latitude</th><th>Longitude</th><th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map((s) => {
                const color = lineColorByStopId.get(s.id);
                return (
                  <tr key={s.id}>
                    <td><code>{s.stop_id}</code></td>
                    <td>{s.stop_code || "—"}</td>
                    <td>
                      {color && <span className="status-dot" style={{ background: `#${color}` }} />}
                      {s.stop_name}
                    </td>
                    <td>{(s.location?.latitude ?? 0).toFixed(5)}</td>
                    <td>{(s.location?.longitude ?? 0).toFixed(5)}</td>
                    <td>
                      <div className="row-actions">
                        {canManage && <button className="btn-pill sm" onClick={() => startEdit(s)}>Edit</button>}
                        {canManage && <button className="btn-pill sm danger" onClick={() => onDelete(s)}>Delete</button>}
                      </div>
                    </td>
                  </tr>
                );
              })}
              {filtered.length === 0 && (
                <tr><td colSpan={6} className="empty">— no matches —</td></tr>
              )}
            </tbody>
          </table>
        </div>
      )}

      {canManage && showCreate && (
        <form className="form-grid network-form" onSubmit={onSubmit}>
          <div className="network-form-label-row">
            <span className="network-form-label">{editingId ? "Edit stop" : "Add stop"}</span>
            <button type="button" className="btn-pill sm" onClick={() => { resetForm(); setShowCreate(false); }}>
              {editingId ? "Cancel" : "Close"}
            </button>
          </div>
          <div className="form-row">
            <label>
              stop_id (GTFS)
              <input value={form.stopId} onChange={(e) => setForm({ ...form, stopId: e.target.value })} placeholder="FAF-01" required />
            </label>
            <label>
              Code (shown on signage)
              <input value={form.stopCode} onChange={(e) => setForm({ ...form, stopCode: e.target.value })} placeholder="01" />
            </label>
            <label style={{ flex: 2 }}>
              Name
              <input value={form.stopName} onChange={(e) => setForm({ ...form, stopName: e.target.value })} placeholder="Faisal Avenue / Jinnah Stop" required />
            </label>
          </div>
          <div className="form-row">
            <label>
              Latitude
              <input value={form.lat} onChange={(e) => setForm({ ...form, lat: e.target.value })} placeholder="33.6938" required />
            </label>
            <label>
              Longitude
              <input value={form.lon} onChange={(e) => setForm({ ...form, lon: e.target.value })} placeholder="73.0651" required />
            </label>
            <button type="submit" className="btn-pill primary" disabled={busy} style={{ alignSelf: "end" }}>
              {busy ? "Saving…" : editingId ? "Save changes" : "+ Add stop"}
            </button>
          </div>
        </form>
      )}
    </SectionCard>
  );
}
